import { isArraySchemaType, isObjectSchemaType } from '../store/JsonSchemaStore';
import findDuplicatedPropertyKeys from 'find-duplicated-property-keys';
import * as uuid from 'uuid';
export var JsonValidationError;
(function (JsonValidationError) {
    JsonValidationError["DuplicateKey"] = "DuplicateKey";
    JsonValidationError["InvalidFormat"] = "InvalidFormat";
    JsonValidationError["EmptyKey"] = "EmptyKey";
})(JsonValidationError || (JsonValidationError = {}));
export class SchemaUtils {
    static jsonSchemaToStructuredOutput(schema) {
        return SchemaUtils.traverseJsonSchema(schema, false, true);
    }
    static structuredOutputToJsonSchema(structuredOutput) {
        const root = SchemaUtils.ensureStructuredRoot(structuredOutput);
        return SchemaUtils.traverseStructuredOutput(root);
    }
    static traverseJsonSchema(schema, isRequired, isRootLevel = false) {
        if (isObjectSchemaType(schema)) {
            return SchemaUtils.traverseSchemaObject(schema, isRequired, isRootLevel);
        }
        if (isArraySchemaType(schema)) {
            return SchemaUtils.traverseSchemaArray(schema, isRequired);
        }
        return SchemaUtils.traverseSchemaPrimitive(schema, isRequired);
    }
    static traverseSchemaObject(schema, isRequired, isRootLevel = false) {
        const rawProperties = schema?.properties;
        const requiredSource = schema?.required;
        const requiredKeys = new Set(Array.isArray(requiredSource) ? requiredSource : []);
        const properties = {};
        const propertyKeys = Object.keys(rawProperties)
            .sort((a, b) => SchemaUtils.sortNodesByPosition(rawProperties[a], rawProperties[b]));
        for (const key of propertyKeys) {
            const childSchema = rawProperties[key];
            const childRequired = requiredKeys.has(key);
            properties[key] = SchemaUtils.traverseJsonSchema(childSchema, childRequired);
        }
        const structured = isRootLevel ? properties : {
            type: 'object',
            required: isRequired,
            properties,
        };
        const description = schema?.description;
        SchemaUtils.assignDescription(structured, description);
        return structured;
    }
    static traverseSchemaArray(schema, isRequired) {
        const structured = {
            type: 'array',
            required: isRequired,
        };
        const schemaAny = schema;
        SchemaUtils.assignDescription(structured, schemaAny ? schemaAny.description : undefined);
        if (schemaAny?.items) {
            if (schemaAny?.items?.type !== 'object') {
                structured.items = schemaAny.items.type;
                return structured;
            }
            const itemsSchema = schemaAny.items;
            structured.items = SchemaUtils.traverseJsonSchema(itemsSchema, true);
        }
        return structured;
    }
    static traverseSchemaPrimitive(schema, isRequired) {
        const schemaAny = schema;
        const type = schemaAny ? SchemaUtils.structuredOutputTypeMapping(schemaAny.type) : undefined;
        const structured = {
            type,
            required: isRequired,
        };
        SchemaUtils.assignDescription(structured, schemaAny ? schemaAny.description : undefined);
        return structured;
    }
    static structuredOutputTypeMapping(type) {
        if (type === 'number') {
            return 'float';
        }
        return type;
    }
    static jsonSchemaTypeMapping(type) {
        if (type === 'float') {
            return 'number';
        }
        return type;
    }
    static traverseStructuredOutput(node) {
        if (!('type' in node)) {
            const structuredRoot = node;
            return SchemaUtils.traverseStructuredRoot(structuredRoot);
        }
        const structuredNode = node;
        const nodeType = SchemaUtils.getStructuredNodeType(structuredNode);
        switch (nodeType) {
            case 'object':
                return SchemaUtils.traverseStructuredObject(structuredNode);
            case 'array':
                return SchemaUtils.traverseStructuredArray(structuredNode);
            default:
                return SchemaUtils.traverseStructuredPrimitive(structuredNode);
        }
    }
    static traverseStructuredRoot(node) {
        const root = {
            type: 'object',
            properties: {},
            required: [],
        };
        for (const [key, child] of Object.entries(node)) {
            if (typeof child !== 'object') {
                continue;
            }
            root.properties[key] = SchemaUtils.traverseStructuredOutput(child);
            if (child.required) {
                root.required.push(key);
            }
        }
        return root;
    }
    static traverseStructuredObject(node) {
        const nodeAny = node;
        const structuredProperties = nodeAny?.properties ? nodeAny.properties : {};
        const properties = {};
        const required = [];
        for (const [key, child] of Object.entries(structuredProperties)) {
            properties[key] = SchemaUtils.traverseStructuredOutput(child);
            const childAny = child;
            if (childAny?.required) {
                required.push(key);
            }
        }
        const schema = {
            type: 'object',
            properties,
            additionalProperties: false,
        };
        SchemaUtils.assignDescription(schema, nodeAny ? nodeAny.description : undefined);
        if (required.length > 0) {
            schema.required = required;
        }
        return schema;
    }
    static traverseStructuredArray(node) {
        const nodeAny = node;
        const schema = {
            type: 'array',
        };
        SchemaUtils.assignDescription(schema, nodeAny ? nodeAny.description : undefined);
        if (nodeAny?.items) {
            if (typeof nodeAny.items !== 'object') {
                schema.items = {
                    type: nodeAny.items,
                };
                return schema;
            }
            const itemsNode = nodeAny.items;
            schema.items = SchemaUtils.traverseStructuredOutput(itemsNode);
        }
        return schema;
    }
    static traverseStructuredPrimitive(node) {
        const nodeAny = node;
        const schema = {
            type: nodeAny ? SchemaUtils.jsonSchemaTypeMapping(nodeAny.type) : undefined,
        };
        SchemaUtils.assignDescription(schema, nodeAny ? nodeAny.description : undefined);
        return schema;
    }
    static ensureStructuredRoot(structuredOutput) {
        const outputAny = structuredOutput;
        if (outputAny?.type) {
            return outputAny;
        }
        const root = structuredOutput;
        return root;
    }
    static getSchemaNodeType(schema) {
        return schema?.type ? schema.type : '';
    }
    static getStructuredNodeType(node) {
        const nodeAny = node;
        return nodeAny?.type ? nodeAny.type : '';
    }
    static assignDescription(target, description) {
        const normalized = SchemaUtils.normalizeDescriptionToSchema(description);
        if (normalized !== undefined) {
            target.description = normalized;
        }
    }
    static normalizeDescriptionToSchema(description) {
        if (description === undefined) {
            return undefined;
        }
        return description;
    }
    static sortNodesByPosition(a, b) {
        return a.__position - b.__position;
    }
    static validateJson(jsonString) {
        const errors = [];
        const hasDuplicates = findDuplicatedPropertyKeys(jsonString);
        const hasEmptyKeys = /"\s*"\s*:/.test(jsonString);
        if (hasDuplicates.length > 0) {
            errors.push(JsonValidationError.DuplicateKey);
        }
        if (hasEmptyKeys) {
            errors.push(JsonValidationError.EmptyKey);
        }
        try {
            const isValid = SchemaUtils.validateJsonStructure(JSON.parse(jsonString));
            if (!isValid) {
                errors.push(JsonValidationError.InvalidFormat);
            }
        }
        catch (e) {
            errors.push(JsonValidationError.InvalidFormat);
        }
        return { valid: errors.length === 0, errors };
    }
    static validateJsonStructure(json) {
        if (typeof json !== 'object' || !json) {
            return false;
        }
        if (!json.type || typeof json.type !== 'string') {
            return false;
        }
        if (!json.properties || typeof json.properties !== 'object') {
            return false;
        }
        if (!Array.isArray(json.required)) {
            return false;
        }
        for (const key of json.required) {
            if (typeof key !== 'string' || !(key in json.properties)) {
                return false;
            }
        }
        return SchemaUtils.validateJsonProperties(json);
    }
    static validateJsonProperties(json) {
        const entries = Object.entries(json.properties);
        for (const [key, value] of entries) {
            if (typeof key !== 'string' || typeof value !== 'object') {
                return false;
            }
            if (!value.type || typeof value.type !== 'string') {
                return false;
            }
            if (value.description && typeof value.description !== 'string') {
                return false;
            }
        }
        return true;
    }
}
//#region Schema Hydration
export function hydrateSchema(node) {
    if (!node || typeof node !== 'object')
        throw new Error('Invalid schema');
    ensureUid(node);
    if (node.type === 'object') {
        initializeObjectSchema(node);
    }
    else if (node.type === 'array') {
        hydrateArrayItems(node);
    }
    return node;
}
function initializeObjectSchema(node) {
    if (!node.properties || typeof node.properties !== 'object')
        node.properties = {};
    if (!Array.isArray(node.required))
        node.required = [];
    if (typeof node.additionalProperties !== 'boolean')
        node.additionalProperties = false;
    assignSchemaPositions(node);
}
function assignSchemaPositions(node) {
    let position = 0;
    for (const k of Object.keys(node.properties)) {
        const child = node.properties[k];
        ensureUid(child);
        hydrateSchema(child);
        Object.defineProperty(child, '__position', {
            value: position++,
            enumerable: false,
            writable: true,
            configurable: true,
        });
    }
}
function hydrateArrayItems(node) {
    if (!node.items || typeof node.items !== 'object') {
        node.items = { type: 'string' };
    }
    ensureUid(node.items);
    hydrateSchema(node.items);
}
export function ensureUid(node, inheritFrom) {
    const uid = inheritFrom?.__uid || `n${uuid.v4()}`;
    if (!Object.prototype.hasOwnProperty.call(node, '__uid')) {
        Object.defineProperty(node, '__uid', { value: uid, enumerable: false, writable: true });
    }
    else if (!node.__uid) {
        node.__uid = uid;
    }
    return node;
}
//#endregion
